import { Component, effect, OnDestroy, OnInit, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { filter, first, Observable, of, Subject, take, takeUntil } from 'rxjs';
import { routes } from './app-routing.module';
import { AuthService } from './auth/services/auth.service';
import { ChatHubConnectionService } from './communication-chat/services/chat-hub-connection.service';
import { fetchPlayerNotifications } from './notification-center/store/actions/profileNotifications.action';
import { Player } from './profile/models/currentPlayer.interface';
import { fetchCurrentPlayerData, fetchSubscriptionRequest } from './profile/store/actions/profileGeneral.action';
import { selectCurrentPlayer } from './profile/store/selectors/profileGeneral.selector';
import { NavigationService } from './shared/services/navigation.service';
import { ProfileMainHubService } from './shared/services/profile-main-hub.service';
import { AppState } from './shared/store/appState';
import { CustomOperators } from './shared/utils/utils';
import { WithdrawalRequestStatusType } from './wallet/models/enums/wallet.enum';
import { WalletActions } from './wallet/store/actions/actions-type.action';
import { fetchWithdrawalRequests } from './wallet/store/actions/wallet.action';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { environment } from '../environments/environment';
import { setRefCode } from './shared/store/actions/commonSettings.action';
import { switchMap } from 'rxjs/operators';
import { HeaderComponent } from './shared/components/header/header.component';
import { PageLoaderComponent } from './shared/components/page-loader/page-loader.component';
import { FloatingSupportComponent } from './shared/components/floating-support/floating-support.component';
import { GdprPanelComponent } from './shared/components/gdpr-panel/gdpr-panel.component';
import { CookieService } from './shared/services/cookie.service';
import { NonClosableModalComponent } from './modals/components/non-closable-modal/non-closable-modal.component';
import { MatDialog } from '@angular/material/dialog';
import {
    fetchPermissionsRequests,
} from './permissions/store/actions/permissions.action';
import { ProfileGeneralActions, ProfileTeamsActions } from './profile/store/actions/actions-type.action';
import { selectProfileTeamsSelector } from './profile/store/selectors/profileTeams.selector';
import { ENTITY_TYPES } from './permissions/models/enums/permission-names';
import { fetchTeamsPermissionsRequest } from './profile/store/actions/profileTeams.action';
import { fetchPlatforms } from './games/store/actions/games.action';


@Component({

    selector: 'app-root',
    templateUrl: 'app.component.html',
    styleUrls: ['app.component.scss'],
    imports: [
        RouterModule,
        HeaderComponent,
        PageLoaderComponent,
        FloatingSupportComponent,
        GdprPanelComponent
    ]
})
export class AppComponent implements OnInit, OnDestroy {
    private translate = inject(TranslateService);
    private store = inject<Store<AppState>>(Store);
    private profileMainHubService = inject(ProfileMainHubService);
    private authService = inject(AuthService);
    private navigationService = inject(NavigationService);
    private chatHubConnectionService = inject(ChatHubConnectionService);
    private router = inject(Router);
    private activatedRoute = inject(ActivatedRoute);
    private cookieService = inject(CookieService);
    private dialog = inject(MatDialog);

    currentPlayer$: Observable<Player> = this.store.select(selectCurrentPlayer);
    refCode: string;
    private destroy$ = new Subject<void>();
    currentPlayerTeams  = this.store.selectSignal(selectProfileTeamsSelector);
    currentPlayer  = this.store.selectSignal(selectCurrentPlayer);
    private readonly effectRef = effect(() => {
        if(this.authService.isAuthorised && this.currentPlayer().profile.playerId) {
            this.store.dispatch(ProfileGeneralActions.fetchSubscriptionRequest({
                payload: {
                    profileId: this.currentPlayer().profile.playerId,
                    query: null
                }
            }));
            const teamsResponse = this.currentPlayerTeams();
            if (teamsResponse?.items?.length) {
                const teamsIds = teamsResponse.items.map(team => team.id);
                teamsIds.forEach(teamId => {
                    const temp_body = {
                        contextEntityType: ENTITY_TYPES.Team,
                        contextEntityId: teamId
                    };
                    this.store.dispatch(fetchTeamsPermissionsRequest({
                        payload: {
                            userId: this.currentPlayer().profile.userId,
                            permissionContexts: [temp_body]
                        }
                    }));
                });

                this.effectRef.destroy();
            }


        }
    });


    constructor() {
        this.translate.setFallbackLang('en');
        this.translate.use('en');
    }

    ngOnInit(): void {

        if (this.isMobile() && this.isInAppBrowser()) {
            this.dialog.open(NonClosableModalComponent, {
                panelClass: 'standard-modal-window-pane',
                data: {
                    modalHeader: 'inAppBrowserWarningTitle?',
                    description: 'inAppBrowserWarningDescription',
                    disableClose: true,

                },
            });
                window.open('googlechrome://navigate?url=https://www.platform.lidoma.com', '_system');
        }
        this.activatedRoute.queryParams.pipe(
            filter(params => !!params.refCode),
            first(),
            switchMap(params =>
                this.authService.isAuthenticated$.pipe(
                    first(),
                    switchMap(isAuthenticated => {
                        if (isAuthenticated) {
                            const now = new Date();
                            this.store.dispatch(setRefCode({
                                payload: {
                                    refCode: this.activatedRoute.snapshot.queryParams.refCode,
                                    firstAccessTimestamp: now.toISOString()
                                }
                            }));
                        } else {
                            const refCode = this.activatedRoute.snapshot.queryParams.refCode;
                            const now = new Date();
                            this.cookieService.setCookie('refCode-lidoma', { refCode, firstAccessTimestamp: now.toISOString() }, 30);
                            this.cookieService.setSimpleCookie('refCode', refCode, 30);
                        }
                        return of(null);
                    })
                )
            ),
            takeUntil(this.destroy$)
        ).subscribe();

        // Track page views with Google Analytics
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            takeUntil(this.destroy$)
        ).subscribe((event: any) => {
            // @ts-ignore
            if (typeof window.gtag === 'function') {
                // @ts-ignore
                window.gtag('config', environment.googleAnalyticKey, {
                    page_path: event.urlAfterRedirects
                });
            }
        });

        // Initialize navigation history
        this.navigationService.startSaveHistory();

        // Handle authentication and player data
        this.authService.isAuthenticated$.pipe(
            CustomOperators.truthy(),
            take(1),
            takeUntil(this.destroy$)
        ).subscribe((isAuth) => {
            if (isAuth) {
                this.store.dispatch(fetchCurrentPlayerData());

                // Handle refCode from cookie if exists
                const refCodeData = this.cookieService.getCookie('refCode-lidoma');
                if (refCodeData) {
                    this.store.dispatch(setRefCode({
                        payload: {
                            refCode: refCodeData.refCode,
                            firstAccessTimestamp: refCodeData.firstAccessTimestamp
                        }
                    }));
                    this.cookieService.deleteCookie('refCode-lidoma');
                }

                // Load player-specific data once player ID is available
                this.currentPlayer$.pipe(
                    filter((player) => !!player.profile.playerId),
                    take(1),
                    takeUntil(this.destroy$)
                ).subscribe((player) => {
                    const playerId = player.profile.playerId;

                    // Fetch permissions
                    this.store.dispatch(
                        fetchPermissionsRequests({
                            payload: player.profile.userId
                        })
                    );

                    // Fetch notifications
                    this.store.dispatch(
                        fetchPlayerNotifications({
                            payload: {
                                playerId,
                                page: 1,
                                pageSize: 10,
                            },
                        })
                    );
                    this.store.dispatch(
                        ProfileTeamsActions.profileGetAllTeams({
                            payload:{
                                profileId: playerId
                            }
                        }));
                    // Connect to hubs
                    this.profileMainHubService.connect();
                    this.chatHubConnectionService.connect(playerId);

                    // Fetch role-specific data
                    if (this.authService.isPlayer) {
                        this.store.dispatch(
                            WalletActions.fetchProfilePaymentAccounts({
                                payload: {
                                    playerId,
                                },
                            })
                        );

                    }

                    if (this.authService.isEmployee) {
                        this.store.dispatch(
                            fetchWithdrawalRequests({
                                payload: {
                                    status: WithdrawalRequestStatusType.Pending,
                                },
                            })
                        );
                    }
                });
            }
        });
        this.store.dispatch(fetchPlatforms());

    }

    isMobile(): boolean {
        return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    }
    isInAppBrowser(): boolean{
        // @ts-ignore
        let ua = navigator.userAgent || navigator.vendor || window.opera;
        let isInstagram = (ua.indexOf('Instagram') > -1) ? true : false;
        // @ts-ignore
        return /Instagram|Discord|Telegram|Twitch|Facebook/i.test(navigator.userAgent) || typeof window.TelegramWebview !== 'undefined' || isInstagram;
    }


    /**
     * Get available loading URLs from routes
     * @returns Array of route paths
     */
    getAvailableLoadingUrls(): (string | undefined)[] {
        return routes.map((r) => r.path);
    }

    /**
     * Clean up subscriptions when component is destroyed
     */
    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
